"use client";

// @ubm/ui/overlays — LE CÂBLAGE D'UN OVERLAY ACCESSIBLE. Une seule implémentation, deux consommateurs :
// l'espace membre (via <Panneau>/<Dialogue>/<Feuille>) et l'administration (via `@ubm/product-ui` Dialog,
// dont l'en-tête reconnaissait la dette : « Focus-trap réel + portail = couche hardening ultérieure »).
//
// Le contrat, tel qu'il est écrit dans le cadre (`espace-membre-composable-cadre.md` §5, « Mécanique —
// jamais composable ») : piège de focus, Échap, retour du focus, portail. Ce crochet en tient QUATRE des
// cinq ; le portail est porté par le composant, parce qu'un consommateur (l'admin) a un DOM déjà en place
// qu'on ne déplace pas.
//
// 🔴 CE QU'IL NE FAIT PAS, ET POURQUOI : il ne REFUSE pas un second overlay. La règle « un seul niveau
// d'overlay » du cadre est tenue à l'ADRESSE (une adresse ne porte qu'une clé de panneau), pas ici : lever
// dans une primitive partagée par ~8 surfaces d'administration transformerait une imbrication — légitime
// aujourd'hui, un sélecteur de média DANS un dialogue de composition — en écran blanc. Ce qu'il fait à la
// place est mesurable : seul l'overlay le PLUS RÉCENT dans le document écoute le clavier, les autres se
// taisent. Une imbrication dégrade, elle ne casse pas, et Échap ne ferme jamais deux panneaux d'un coup.
//
// ⚠️ IL REND UNE RÉFÉRENCE DE RAPPEL, PAS UN `useRef`, et c'est une correction MESURÉE. Avec un `useRef`,
// les effets ne se rejouaient jamais pour un overlay monté en DEUX temps — et c'est le cas de TOUS les
// overlays à portail, qui n'émettent rien tant que `document` n'est pas là. Le banc l'a attrapé : le
// crochet ne posait ni piège ni inertie sur les trois primitives, alors qu'il les posait sur une scène
// sans portail. Une référence de rappel fait du nœud une DÉPENDANCE d'effet ; c'est ce qui rend le
// montage différé indifférent.
//
// ENGAGEMENT D'HÔTE (lot U2, 05/10/2026). Le crochet lit `useOverlayHostEngaged()` (voir `OverlayHost.tsx`). Un overlay
// ouvert sous un hôte DÉSENGAGÉ (fenêtre en arrière-plan ou masquée) reste marqué et monté, mais ne piège plus, n'inerte
// plus, ne verrouille plus, n'écoute plus le clavier et ne prend ni ne rend le focus : seul l'état ouvert logique reste.
// Le désengagement n'exécute AUCUN nettoyage de focus (le focus de l'hôte actif n'est jamais volé) ; le réengagement
// replace le focus dans l'overlay sans perdre l'origine retenue à l'ouverture.
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type RefCallback, type RefObject } from "react";
import { cibleTabulation, elementsFocalisables, premierFocus } from "./focus";
import {
  ATTRIBUT_ENGAGEMENT,
  attributsEngagement,
  consommerEchap,
  echapConsomme,
  estCoucheEngagee,
  useOverlayHostEngaged,
} from "./OverlayHost";

// `useLayoutEffect` côté serveur (pré-rendu) : variante isomorphe.
const useIsoLayoutEffect = typeof window !== "undefined" ? useLayoutEffect : useEffect;

/** Marque posée sur le conteneur de CHAQUE overlay : c'est elle qui ordonne la pile et porte l'inertie. */
export const ATTRIBUT_OVERLAY = "data-ubm-overlay";

// ── LES COUCHES NON MODALES (lot K, 04/10/2026) ─────────────────────────────────────────────────────────
//
// Mesuré en Chromium : un Select (Radix) ouvert dans le Dialog d'administration, premier Échap → le Dialog ET la liste
// fermés. Le crochet écoute en capture sur `document` et la liste ne portait aucune marque : le Dialog se croyait seul
// au-dessus. Même famille pour <FloatingPanel> et <SidePeek>, qui écoutaient Échap sans aucune notion de couche.
//
// Une couche non modale (liste, popup, panneau latéral) prend donc SON rang dans la même pile, avec la même marque,
// valeur `non-modal`, posée sur sa vraie racine visible et rendue seulement tant qu'elle est montée. Elle n'adopte ni
// piège de focus, ni inertie, ni verrou de défilement : seul l'ordre du clavier est commun. Deux règles en découlent :
//   · une couche non modale N'EST PAS exemptée de l'inertie qu'un overlay modal pose derrière lui (comportement
//     d'avant inchangé) — et une couche ainsi rendue inerte n'est plus « au-dessus » ;
//   · un Échap déjà consommé (`defaultPrevented`, posé par la couche qui s'est fermée — Radix compris) ne ferme plus
//     rien d'autre, même quand l'enfant s'est démonté pendant l'événement. Lot U2 : un Échap seulement CÉDÉ par une
//     couche Radix qui ne s'est pas fermée (`cederEchap`) n'est pas consommé.

/** Valeur de la marque d'une couche NON modale : rang dans la pile du clavier, sans piège, inertie ni verrou. */
export const OVERLAY_NON_MODAL = "non-modal";

/** À étaler sur la vraie racine visible d'une couche non modale, rendue seulement quand elle est ouverte. */
export const marqueOverlayNonModal = { [ATTRIBUT_OVERLAY]: OVERLAY_NON_MODAL } as const;

/**
 * Posé sur le voile décoratif d'un overlay modal (frère du conteneur, sous le même portail) : il n'est ni rendu inerte
 * ni masqué par l'overlay qu'il accompagne — il reste la cible du clic qui ferme.
 */
export const ATTRIBUT_VOILE = "data-ubm-overlay-backdrop";

/** Un overlay MODAL (marqué, hors `non-modal`) : le seul exempté de l'inertie posée par un autre. */
const SELECTEUR_MODAL = `[${ATTRIBUT_OVERLAY}]:not([${ATTRIBUT_OVERLAY}="${OVERLAY_NON_MODAL}"])`;

/** Une couche NON modale marquée (racine visible d'une liste, d'un popup, d'un peek). */
const SELECTEUR_NON_MODAL = `[${ATTRIBUT_OVERLAY}="${OVERLAY_NON_MODAL}"]`;

// ── APPARTENANCE LOGIQUE D'UNE COUCHE NON MODALE (correctif Échap après réengagement, 05/10/2026) ───────────────────
//
// Mesuré (scène Dates, Chromium) : un popup <FloatingPanel> ouvert DANS un Dialog, porté sous `<body>` après lui, puis
// hôte désengagé → réengagé. L'inertie reposée au réengagement le traitait comme un frère d'arrière-plan
// (`aria-hidden` + `inert`) : `estOverlayDuDessus` l'écartait à juste titre, son champ ne reprenait pas le focus, et le
// premier Échap fermait le Dialog au lieu du popup. Au premier passage, le popup n'existait pas encore quand l'inertie
// était posée : le défaut ne touchait que le réengagement.
// Relation PRIVÉE racine non modale → ancre, déjà fournie à `useCoucheNonModale`, tenue tant que la couche est ouverte.
// Elle sert UNIQUEMENT à exempter de l'inertie d'un modal la couche qui lui appartient logiquement (ancre dans le
// modal, directement ou via la chaîne des ancres de couches portées parentes). Le fond et les couches étrangères
// restent inertes ; la pile, les phases et les contrats d'Échap sont inchangés. Ni pile, ni rang, ni API ajoutés.
const ancresNonModales = new WeakMap<Element, Element>();

/** La couche non modale `couche` appartient-elle logiquement à la racine modale `modale` (chaîne des ancres) ? */
function appartientAuModal(couche: Element, modale: Element): boolean {
  const vues = new Set<Element>();
  let courante: Element | null = couche;
  while (courante !== null && !vues.has(courante)) {
    vues.add(courante);
    const ancre = ancresNonModales.get(courante);
    if (ancre === undefined || !ancre.isConnected) return false;
    if (modale.contains(ancre)) return true;
    // Ancre posée dans une autre couche portée (popup dans un popup) : on suit l'ancre de cette couche.
    courante = ancre.closest(SELECTEUR_NON_MODAL);
  }
  return false;
}

/** `el` est-il, ou contient-il, une couche non modale appartenant à `modale` ? */
function porteCoucheDuModal(el: Element, modale: Element): boolean {
  if (el.matches(SELECTEUR_NON_MODAL) && appartientAuModal(el, modale)) return true;
  return Array.from(el.querySelectorAll(SELECTEUR_NON_MODAL)).some((c) => appartientAuModal(c, modale));
}

/**
 * `racine` est-elle la couche du dessus ? Dernière marquée dans l'ordre du document — un portail sous `<body>` ouvert
 * plus tard passe donc devant —, en écartant les couches non modales rendues inertes par un overlay modal, et (lot U2)
 * toute couche d'un hôte désengagé : elle reste montée, mais ne prend plus son rang.
 */
export function estOverlayDuDessus(racine: Element): boolean {
  const ecartee = (el: Element) =>
    !estCoucheEngagee(el) || (el.getAttribute(ATTRIBUT_OVERLAY) === OVERLAY_NON_MODAL && el.closest("[inert]") !== null);
  // La règle vaut AUSSI pour la couche qui interroge : une couche écartée n'est jamais au-dessus.
  if (ecartee(racine)) return false;
  const pile = Array.from(racine.ownerDocument.querySelectorAll(`[${ATTRIBUT_OVERLAY}]`)).filter((el) => !ecartee(el));
  return pile.length === 0 || pile[pile.length - 1] === racine;
}

export interface OverlayAccessibleOptions {
  /** L'overlay est-il monté et visible ? */
  ouvert: boolean;
  /** Demande de fermeture (Échap). Le composant décide ce que « fermer » veut dire (retour, annuler…). */
  onFermer?: () => void;
  /** Piéger le focus dans le conteneur. Défaut `true` — un overlay modal le doit. */
  piegeFocus?: boolean;
  /** Échap ferme. Défaut `true`. */
  echapFerme?: boolean;
  /** Bloquer le défilement du document derrière. Défaut `true`. */
  verrouilleDefilement?: boolean;
  /**
   * Retirer l'arrière-plan de l'arbre d'accessibilité (`aria-hidden` + `inert`). Défaut `true`. C'est LE
   * critère « lecteur d'écran » : sans lui, le curseur virtuel continue de lire la page derrière, et aucun
   * banc de DOM qui ne regarde que le clavier ne s'en aperçoit.
   */
  inertieArrierePlan?: boolean;
  /**
   * Rendre le focus à l'élément d'origine à la fermeture. Défaut `true`. Mettre `false` quand la fermeture
   * navigue (l'élément d'origine n'existe plus après un retour d'historique).
   */
  rendLeFocus?: boolean;
  /**
   * Poser le focus dans le conteneur à l'entrée. Défaut `true`. Mettre `false` quand une bibliothèque (Radix
   * `FocusScope`) porte déjà l'autofocus du conteneur : un seul propriétaire de l'entrée.
   */
  focusEntree?: boolean;
  /** Forcer l'engagement (sinon lu de `useOverlayHostEngaged()`). */
  engage?: boolean;
  /**
   * Lot U2 (correction) — rappels ANNULABLES d'un moteur de composant (sémantique Radix `onEscapeKeyDown`,
   * `onOpenAutoFocus`, `onCloseAutoFocus`) : appelés AVANT l'effet ; rendre `false` l'empêche.
   * `avantEchap` reçoit l'Échap natif encore libre (un `preventDefault()` de l'appelant le consomme et refuse la fermeture).
   * `avantFocusEntree` : une fois par ouverture, à la première activation. `avantRetourFocus` : au retour du focus.
   */
  avantEchap?: (event: KeyboardEvent) => boolean;
  avantFocusEntree?: () => boolean;
  avantRetourFocus?: () => boolean;
}

// Plusieurs overlays peuvent posséder la même ressource. Restaurer uniquement au dernier
// retrait préserve l'état initial, même si une couche inférieure ferme avant la supérieure.
const verrousDefilement = new WeakMap<HTMLElement, { nombre: number; avant: string }>();
const verrousInertie = new WeakMap<Element, { nombre: number; aria: string | null; inerte: boolean }>();
function retenirInertie(el: Element): () => void {
  let verrou = verrousInertie.get(el);
  if (verrou) verrou.nombre++;
  else {
    verrou = { nombre: 1, aria: el.getAttribute("aria-hidden"), inerte: el.hasAttribute("inert") };
    verrousInertie.set(el, verrou);
    el.setAttribute("aria-hidden", "true");
    el.setAttribute("inert", "");
  }
  return () => {
    if (--verrou.nombre > 0) return;
    if (verrou.aria === null) el.removeAttribute("aria-hidden");
    else el.setAttribute("aria-hidden", verrou.aria);
    if (!verrou.inerte) el.removeAttribute("inert");
    verrousInertie.delete(el);
  };
}

/** L'élément réellement focalisé. */
function actifCourant(doc: Document): HTMLElement | null {
  const el = doc.activeElement;
  return el instanceof HTMLElement ? el : null;
}

/**
 * Câble un conteneur d'overlay. Rend la référence à poser sur l'élément `role="dialog"`.
 *
 * ```tsx
 * const ref = useOverlayAccessible<HTMLDivElement>({ ouvert, onFermer });
 * return <div ref={ref} role="dialog" aria-modal="true" aria-labelledby={id}>…</div>;
 * ```
 */
export function useOverlayAccessible<T extends HTMLElement>(
  options: OverlayAccessibleOptions,
): RefCallback<T> {
  const hote = useOverlayHostEngaged();
  const {
    ouvert,
    onFermer,
    piegeFocus = true,
    echapFerme = true,
    verrouilleDefilement = true,
    inertieArrierePlan = true,
    rendLeFocus = true,
    focusEntree = true,
    engage = hote,
  } = options;
  // Actif = ouvert ET hôte engagé. Ouvert sous un hôte désengagé : marqué, monté, mais suspendu.
  const actif = ouvert && engage;

  const [racine, setRacine] = useState<T | null>(null);
  const attacher = useCallback<RefCallback<T>>((el) => setRacine(el), []);

  // `onFermer` change à chaque rendu chez la plupart des appelants (fonction fléchée inline). On le lit
  // par une boîte pour que l'écouteur clavier ne soit pas reposé — et surtout pour que l'effet de FOCUS
  // ne rejoue pas, ce qui ramènerait le focus au premier contrôle à chaque frappe.
  const fermerRef = useRef(onFermer);
  fermerRef.current = onFermer;
  const rappelsRef = useRef(options);
  rappelsRef.current = options;
  // L'engagement lu AU MOMENT d'un nettoyage : rendre le focus n'a lieu que sous un hôte engagé.
  const engageRef = useRef(engage);
  engageRef.current = engage;

  // ── 1. L'entrée : marquer l'overlay tant qu'il est ouvert (engagé ou non) ─────────────────────────
  useEffect(() => {
    if (!ouvert || racine === null) return;
    racine.setAttribute(ATTRIBUT_OVERLAY, "");
    if (!racine.hasAttribute("tabindex")) racine.setAttribute("tabindex", "-1");
    return () => racine.removeAttribute(ATTRIBUT_OVERLAY);
  }, [ouvert, racine]);

  // ── 1 bis. Désengagé : la racine le porte, et sort du classement de la pile commune ──────────────
  useEffect(() => {
    if (!ouvert || racine === null || engage) return;
    racine.setAttribute(ATTRIBUT_ENGAGEMENT, "false");
    return () => racine.removeAttribute(ATTRIBUT_ENGAGEMENT);
  }, [ouvert, racine, engage]);

  // ── 1 ter. L'origine, retenue UNE fois par ouverture, AVANT tout autofocus ───────────────────────
  //
  // Effet de MISE EN PAGE, sans dépendre de la racine : une bibliothèque montée DANS l'overlay (Radix `FocusScope`)
  // pose son autofocus dans un effet passif ENFANT, qui passe avant les effets passifs de ce crochet — et la racine,
  // posée par référence de rappel, n'arrive qu'au rendu suivant. Lue ici, l'origine est encore le déclencheur.
  const origineRef = useRef<HTMLElement | null>(null);
  const origineRetenueRef = useRef(false);
  const racineRef = useRef<T | null>(null);
  racineRef.current = racine;
  useIsoLayoutEffect(() => {
    // Première activation de cette ouverture : on retient d'où l'on vient. Un réengagement ne l'écrase pas.
    if (!ouvert || !engage || origineRetenueRef.current || typeof document === "undefined") return;
    origineRetenueRef.current = true;
    const actuel = actifCourant(racineRef.current?.ownerDocument ?? document);
    origineRef.current = actuel !== null && racineRef.current?.contains(actuel) ? null : actuel;
  }, [ouvert, engage]);

  // ── 1 quater. Le focus d'entrée ───────────────────────────────────────────────────────────────────
  // Le rappel annulable n'est consulté qu'à la PREMIÈRE activation d'une ouverture (comme un autofocus au montage) ;
  // un réengagement replace seulement le focus dedans s'il n'y est pas.
  const entreeFaiteRef = useRef(false);
  useEffect(() => {
    if (!actif || racine === null || !focusEntree) return;
    const premiere = !entreeFaiteRef.current;
    entreeFaiteRef.current = true;
    if (premiere && rappelsRef.current.avantFocusEntree && !rappelsRef.current.avantFocusEntree()) return;
    const actuel = actifCourant(racine.ownerDocument);
    // Focus déjà dans l'overlay (réengagement, ou appelant qui l'a posé) : on ne le déplace pas.
    if (actuel === null || !racine.contains(actuel)) premierFocus(racine).focus();
  }, [actif, racine, focusEntree]);

  // ── 2. Le clavier : Échap ferme, Tab ne sort pas ───────────────────────────────────────────────────
  useEffect(() => {
    if (!actif || racine === null) return;
    const doc = racine.ownerDocument;

    const auClavier = (e: KeyboardEvent) => {
      // Une couche au-dessus (modale ou non : liste Radix, popup, peek) garde TOUT son clavier — Échap comme Tab.
      if (!estOverlayDuDessus(racine)) return;
      if (echapFerme && e.key === "Escape") {
        // Déjà consommé par la couche qui s'est fermée : un seul Échap, un seul panneau. Un Échap seulement
        // CÉDÉ par une couche Radix (qui ne s'est pas fermée) reste à prendre.
        if (echapConsomme(e)) return;
        // Rappel annulable de l'appelant (Radix `onEscapeKeyDown`) : son `preventDefault()` consomme et refuse.
        const avant = rappelsRef.current.avantEchap;
        if (avant && !avant(e)) return;
        consommerEchap(e);
        // Un overlay imbriqué ne doit pas voir sa fermeture remonter jusqu'au parent : un seul Échap,
        // un seul panneau fermé.
        e.stopPropagation();
        fermerRef.current?.();
        return;
      }
      if (!piegeFocus || e.key !== "Tab") return;
      const cible = cibleTabulation(elementsFocalisables(racine), actifCourant(doc), e.shiftKey);
      // Liste vide : le conteneur lui-même (tabindex=-1) garde le focus. On empêche quand même la
      // tabulation native, sinon le focus part derrière l'overlay — le défaut exact qu'on répare.
      e.preventDefault();
      cible?.focus();
    };

    doc.addEventListener("keydown", auClavier, true);
    return () => doc.removeEventListener("keydown", auClavier, true);
  }, [actif, racine, piegeFocus, echapFerme]);

  // ── 3. Le lecteur d'écran : l'arrière-plan sort de l'arbre d'accessibilité ─────────────────────────
  //
  // On REMONTE depuis l'overlay jusqu'à `<body>` en retirant, à chaque étage, les frères du chemin. Un
  // simple balayage des enfants de `<body>` ne suffirait pas : quand l'overlay n'est PAS dans un portail
  // — c'est le cas du Dialog de l'administration — il partage son parent avec le contenu à masquer, et
  // l'unique enfant de `<body>` contient alors les deux. Mesuré au banc : rien n'était masqué.
  useEffect(() => {
    if (!actif || !inertieArrierePlan || racine === null) return;
    const doc = racine.ownerDocument;
    const restitutions: (() => void)[] = [];

    const rendre = (el: Element) => {
      restitutions.push(retenirInertie(el));
    };
    // Une couche non modale qui APPARTIENT à cet overlay (voir `appartientAuModal`) n'est pas un arrière-plan : elle
    // reste accessible. Un frère qui la CONTIENT sans l'être est parcouru : ses autres enfants restent masqués.
    const masquer = (el: Element) => {
      if (!porteCoucheDuModal(el, racine)) return rendre(el);
      if (el.matches(SELECTEUR_NON_MODAL) && appartientAuModal(el, racine)) return;
      for (const enfant of Array.from(el.children)) masquer(enfant);
    };

    let courant: Element | null = racine;
    while (courant !== null && courant !== doc.body && courant.parentElement !== null) {
      for (const frere of Array.from(courant.parentElement.children)) {
        if (frere === courant) continue;
        // Un autre overlay MODAL (ou son porteur) n'est jamais masqué : une imbrication dégrade, elle ne
        // s'aveugle pas elle-même. Une couche non modale reste masquée, comme avant qu'elle porte la marque,
        // SAUF celle qui appartient logiquement à cet overlay (réengagement d'un popup ouvert dedans).
        if (frere.matches(SELECTEUR_MODAL) || frere.querySelector(SELECTEUR_MODAL) !== null) continue;
        // Le voile de CET overlay (frère sous le même portail) reste la cible du clic qui ferme.
        if (frere.hasAttribute(ATTRIBUT_VOILE)) continue;
        masquer(frere);
      }
      courant = courant.parentElement;
    }

    return () => {
      for (const restituer of restitutions) restituer();
    };
  }, [actif, racine, inertieArrierePlan]);

  // ── 4. Le défilement du document ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!actif || !verrouilleDefilement || racine === null) return;
    const body = racine.ownerDocument.body;
    let verrou = verrousDefilement.get(body);
    if (verrou) verrou.nombre++;
    else {
      verrou = { nombre: 1, avant: body.style.overflow };
      verrousDefilement.set(body, verrou);
      body.style.overflow = "hidden";
    }
    return () => {
      if (--verrou.nombre > 0) return;
      body.style.overflow = verrou.avant;
      verrousDefilement.delete(body);
    };
  }, [actif, racine, verrouilleDefilement]);

  // ── 5. LE RETOUR DU FOCUS — déclaré EN DERNIER, et c'est la raison d'être de cet ordre ────────────
  //
  // React exécute les nettoyages dans l'ordre de DÉCLARATION. Rendre le focus doit se faire APRÈS que
  // l'inertie a été retirée (effet 3) : un élément encore `inert` ou `aria-hidden` ne prend pas le focus,
  // et le document retombe sur `<body>`. Le banc l'a mesuré tel quel — « Échap ferme, et le focus REVIENT
  // à l'élément d'origine » rendait `<body>` tant que ce nettoyage précédait celui de l'inertie. C'est
  // aussi ce qui se passerait dans un vrai navigateur : la correction n'est pas une astuce de banc.
  //
  // Lot U2 : l'effet dépend de `ouvert`, pas de l'engagement — se désengager ne le nettoie donc pas. Une fermeture ou
  // un démontage sous un hôte désengagé ne rend RIEN : le focus de l'hôte actif n'est jamais volé.
  useEffect(() => {
    if (!ouvert || racine === null || !rendLeFocus) return;
    return () => {
      if (!engageRef.current) return;
      const avant = rappelsRef.current.avantRetourFocus;
      if (avant && !avant()) return;
      const origine = origineRef.current;
      // L'origine peut avoir quitté le document (liste re-rendue, navigation) : on ne force alors rien.
      if (origine !== null && origine.isConnected) origine.focus();
    };
  }, [ouvert, racine, rendLeFocus]);

  // Fermé : l'origine est oubliée — dans un effet PASSIF, donc après le nettoyage ci-dessus qui la lit encore.
  useEffect(() => {
    if (ouvert) return;
    origineRef.current = null;
    origineRetenueRef.current = false;
    entreeFaiteRef.current = false;
  }, [ouvert]);

  return attacher;
}

// ── LE MOTEUR NON MODAL COMMUN (lot U2, 05/10/2026) ─────────────────────────────────────────────────────
//
// Le rejet d'une couche NON modale (clic extérieur logique + Échap de la couche du dessus), tel que <FloatingPanel> le
// tenait seul depuis E1/RN02/K, remonté ici pour que TOUTES les couches non modales non Radix le réemploient — FloatingPanel,
// SidePeek, ContextMenu et UserMenu (sources UIArc) — au lieu de poser chacune leurs écouteurs `document`. Aucun manager ni
// pile propre : le rang vient de la marque commune (`estOverlayDuDessus`), l'appartenance logique de l'IDENTITÉ de
// l'événement natif mémorisé par la capture React de la racine (un descendant React rendu dans son propre portail —
// liste d'un Select Radix — est intérieur ; le portail d'une autre instance reste extérieur).
//
// Sous un hôte désengagé, la couche ne s'écoute plus : ni clic extérieur, ni Échap, ni rappel `onFermer` induit.

/** Pourquoi une couche non modale demande sa fermeture. */
export type RaisonFermeture = "exterieur" | "echap";

export interface CoucheNonModaleOptions {
  /** La couche est-elle ouverte (montée) ? */
  ouvert: boolean;
  /** Demande de fermeture, avec sa raison (clic extérieur ou Échap) et l'événement natif qui la porte. */
  onFermer: (raison: RaisonFermeture, evenement: Event) => void;
  /** La vraie racine visible de la couche (dans son portail le cas échéant). */
  racine: RefObject<HTMLElement | null>;
  /** Élément dont un appui ne compte jamais comme extérieur (ancre, déclencheur). */
  ancre?: RefObject<HTMLElement | null>;
  /** Événement du clic extérieur, ou `false` (aucun : un peek ferme par son voile). Défaut `"mousedown"`. */
  exterieur?: "mousedown" | "pointerdown" | false;
  /** Écouter l'appui extérieur en phase de capture. Défaut `false`. */
  captureExterieur?: boolean;
  /** Échap ferme la couche du dessus. Défaut `true`. */
  echap?: boolean;
  /** Arrêter la propagation de l'Échap pris (au-delà de `document`). Défaut `false`. */
  arreterPropagation?: boolean;
  /** Forcer l'engagement (sinon lu de `useOverlayHostEngaged()`). */
  engage?: boolean;
}

export interface CoucheNonModale {
  /** L'hôte de la couche est-il engagé ? */
  engagee: boolean;
  /** À poser en `onMouseDownCapture` / `onPointerDownCapture` sur la racine React de la couche. */
  marquerInterieur: (event: { nativeEvent: Event }) => void;
  /** À étaler sur la racine : marque non modale, et désengagement le cas échéant. */
  attributs: { [ATTRIBUT_OVERLAY]: typeof OVERLAY_NON_MODAL; [ATTRIBUT_ENGAGEMENT]?: "false" };
}

/** Rejet commun d'une couche non modale (voir l'en-tête de section). */
export function useCoucheNonModale(options: CoucheNonModaleOptions): CoucheNonModale {
  const hote = useOverlayHostEngaged();
  const {
    ouvert,
    onFermer,
    racine,
    ancre,
    exterieur = "mousedown",
    captureExterieur = false,
    echap = true,
    arreterPropagation = false,
    engage = hote,
  } = options;
  const actif = ouvert && engage;
  const fermerRef = useRef(onFermer);
  fermerRef.current = onFermer;

  // Relation privée racine → ancre (voir `appartientAuModal`) : tenue tant que la couche est OUVERTE (engagée ou non),
  // reposée à chaque commit en effet de mise en page — donc prête avant l'effet d'inertie (passif) d'un modal qui se
  // réengage — et retirée à la fermeture comme au démontage.
  useIsoLayoutEffect(() => {
    const el = racine.current;
    const a = ancre?.current ?? null;
    if (!ouvert || el === null || a === null) return;
    ancresNonModales.set(el, a);
    return () => {
      if (ancresNonModales.get(el) === a) ancresNonModales.delete(el);
    };
  });

  // Appartenance LOGIQUE : l'événement natif vu par la capture React de la racine (identité, pas un drapeau).
  const interieurRef = useRef<Event | null>(null);
  const marquerInterieur = useCallback((event: { nativeEvent: Event }) => {
    interieurRef.current = event.nativeEvent;
  }, []);

  // Lot U2 (correction) — en mode CAPTURE, l'écouteur `document` passe AVANT la capture React qui marque l'événement
  // d'un descendant porté (`marquerInterieur`) : juger là exclurait à tort ce descendant. La capture ne fait donc que
  // RETENIR l'événement ; le jugement a lieu à son passage en bulle sur `document` (après React), ou, si une couche a
  // arrêté la propagation, au tour suivant (minuterie 0) — c'est ce que la capture garantissait déjà : un
  // `stopPropagation` ailleurs n'empêche pas la fermeture. Même identité d'événement natif, aucun registre.
  useEffect(() => {
    if (!actif || exterieur === false) return;
    let enAttente: Event | null = null;
    let minuterie = 0;
    const juger = (e: Event) => {
      if (enAttente !== e) return;
      enAttente = null;
      window.clearTimeout(minuterie);
      const logique = interieurRef.current === e;
      interieurRef.current = null;
      const cible = e.target as Node | null;
      if (cible !== null && ancre?.current?.contains(cible)) return;
      if (cible !== null && racine.current?.contains(cible)) return;
      if (logique) return;
      fermerRef.current("exterieur", e);
    };
    const retenir = (e: Event) => {
      enAttente = e;
      window.clearTimeout(minuterie);
      minuterie = window.setTimeout(() => juger(e), 0);
    };
    const enBulle = (e: Event) => {
      if (!captureExterieur) enAttente = e;
      juger(e);
    };
    if (captureExterieur) document.addEventListener(exterieur, retenir, true);
    document.addEventListener(exterieur, enBulle);
    return () => {
      if (captureExterieur) document.removeEventListener(exterieur, retenir, true);
      document.removeEventListener(exterieur, enBulle);
      window.clearTimeout(minuterie);
    };
  }, [actif, exterieur, captureExterieur, ancre, racine]);

  useEffect(() => {
    if (!actif || !echap) return;
    // Échap déjà CONSOMMÉ par une couche plus haute (Radix `DismissableLayer` — liste d'un Select ouvert — l'écoute en
    // capture sur document et appelle `preventDefault()` quand il se ferme) : seule cette couche se ferme. Une couche
    // marquée au-dessus garde aussi son Échap ; fermer consomme l'événement. Un Échap CÉDÉ par un parent Radix reste libre.
    const surTouche = (e: KeyboardEvent) => {
      if (e.key !== "Escape" || echapConsomme(e)) return;
      const el = racine.current;
      if (el !== null && !estOverlayDuDessus(el)) return;
      consommerEchap(e);
      if (arreterPropagation) e.stopPropagation();
      fermerRef.current("echap", e);
    };
    document.addEventListener("keydown", surTouche);
    return () => document.removeEventListener("keydown", surTouche);
  }, [actif, echap, arreterPropagation, racine]);

  return {
    engagee: engage,
    marquerInterieur,
    attributs: { [ATTRIBUT_OVERLAY]: OVERLAY_NON_MODAL, ...attributsEngagement(engage) },
  };
}
